import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classnames from 'classnames';
import React__default, { useState, useEffect } from 'react';
import { setFirstColumnAsShouldSort } from '../utils/columnUtils.js';
import { SET_SORTED_DISPLAY_DATA, SET_CLIENT_PAGINATION, SET_GROUP_CHILD_ROWS_ON_PAGE } from '../utils/constants.js';
import { useTable } from '../utils/hooks.js';
import { sliceRowsByItemRange, getUniqueGroupValuesByField } from '../utils/rowUtils.js';
import { sortData } from '../utils/sortUtils.js';
import RecursiveRowGroups from './RecursiveRowGroups.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

/**
 * FirstParentRecursiveRowGroup - The first parent is important that it takes the first row group column def and builds unique data for its children. It also passes down the groupId(First row group column def id) so that the children can reference as its first parent group ID.
 * @param {object} props
 * @param {displayData} props.displayData
 * @param {columns} props.columns
 * @param {isClient} props.isClient
 * @param {isServer} props.isServer
 * @param {pagination} props.pagination
 * @returns {any}
 */

var FirstParentRecursiveRowGroup = function FirstParentRecursiveRowGroup() {
  var _useTable = useTable(),
    dispatch = _useTable.dispatch,
    state = _useTable.state;
  var displayData = state.displayData,
    columns = state.columns,
    isClient = state.isClient,
    isServer = state.isServer,
    pagination = state.pagination;
  var itemRange = pagination.itemRange;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    groupData = _useState2[0],
    setGroupData = _useState2[1];
  var columnDefsRef = columns.columnDefs.map(function (def) {
    return def.sortDirection;
  }).toString();

  // Row groups UX
  // Update columns sort rules
  // First column always needs to be sorted otherwise applying sort on the children could re order the parent
  // TODO could this logic be moved to the reducer
  var columnDefsWithFirstColumnSorted = setFirstColumnAsShouldSort(columns.columnDefs);

  // Sort at the first parent level
  var sortedDisplayData = sortData(groupData, [columnDefsWithFirstColumnSorted === null || columnDefsWithFirstColumnSorted === void 0 ? void 0 : columnDefsWithFirstColumnSorted[0]], groupData);

  // Slice rows for pagination
  var slicedRowData = sliceRowsByItemRange(sortedDisplayData, (itemRange === null || itemRange === void 0 ? void 0 : itemRange.start) - 1, itemRange === null || itemRange === void 0 ? void 0 : itemRange.end, isClient);

  // Used for setting unique keys in children that relate to the parent
  var firstParentField = columns.columnDefs[0].field;

  // Set and sort whenever columns change for non groups rows
  // Set data for group rows if it hasn't been set
  useEffect(function () {
    if (!columns.hasRowGroup) {
      dispatch({
        type: SET_SORTED_DISPLAY_DATA
      });
    }
  }, [columnDefsRef, columns.hasRowGroup, dispatch]);
  useEffect(function () {
    if (columns.hasRowGroup && Array.isArray(displayData)) {
      var uniqueGroupValues = getUniqueGroupValuesByField(displayData, columns.columnDefs[0].field);

      // Set group data locally
      setGroupData(uniqueGroupValues);

      // Update client side pagination to use group row count instead of rows
      dispatch({
        payload: _objectSpread(_objectSpread({}, state), {}, {
          //send back full state so it doesn't clear selectedRows
          pagination: {
            totalItems: uniqueGroupValues.length
          }
        }),
        type: SET_CLIENT_PAGINATION
      });
    }
  }, [displayData, columns.columnDefs, dispatch, columns.hasRowGroup, isServer]);
  useEffect(function () {
    if (columns.hasRowGroup && Array.isArray(displayData) && groupData.length > 0) {
      var pageGroupChildRows = displayData.filter(function (row) {
        return slicedRowData.some(function (group) {
          return group[firstParentField] === row[firstParentField];
        });
      });
      dispatch({
        //send back full state so it doesn't clear selectedRows
        payload: _objectSpread(_objectSpread({}, state), {}, {
          groupChildRowsOnPage: pageGroupChildRows
        }),
        type: SET_GROUP_CHILD_ROWS_ON_PAGE
      });
    }
  }, [groupData, displayData, JSON.stringify(slicedRowData)]);

  // TODO: consolidate into column util
  var classesForColumns = columns.columnDefs.reduce(function (obj, _ref) {
    var field = _ref.field,
      align = _ref.align,
      pinned = _ref.pinned;
    return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, field, classnames(_defineProperty(_defineProperty({}, "--text-align-".concat(align), align), '--pinned', pinned))));
  }, {});
  return slicedRowData.map(function (rowGroupValue, index) {
    var rowGroupValueFirstParentField = rowGroupValue[firstParentField];
    return /*#__PURE__*/React__default.createElement(RecursiveRowGroups, {
      id: index,
      columns: columns.columnDefs,
      classesForColumns: classesForColumns,
      key: rowGroupValueFirstParentField,
      parentRows: displayData,
      parentValue: rowGroupValueFirstParentField,
      parentRowGroupIndex: columns.columnDefs[0].index,
      groupId: rowGroupValueFirstParentField
    });
  });
};

export { FirstParentRecursiveRowGroup as default };
